import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AlertCircle, ChevronRight, Circle, ListTree } from "lucide-react";

import IconButton from "./IconButton";

const expandedIds = (items) => items.flatMap((item) => [
  ...(item.defaultExpanded ? [item.id] : []),
  ...expandedIds(item.children || []),
]);

const treeErrorCount = (items) => items.reduce(
  (total, item) => total + (item.errorCount || 0) + treeErrorCount(item.children || []),
  0,
);

const FieldTreeBranch = ({ activeId, collapsibleDepth, item, level, onSelect, onToggle, openIds }) => {
  const children = item.children || [];
  const hasChildren = children.length > 0;
  const collapsible = hasChildren && level <= collapsibleDepth;
  const expanded = hasChildren && (!collapsible || openIds.has(item.id));
  const Icon = item.icon;

  return (
    <li
      className={["wsm-field-tree__branch", level > 0 && "wsm-field-tree__branch--nested"].filter(Boolean).join(" ")}
      style={{ "--field-tree-branch-line-offset": `${30 + ((level - 1) * 28)}px` }}
    >
      <div
        className={[
          "wsm-field-tree__row",
          level > 0 && "wsm-field-tree__row--nested",
          collapsible && "wsm-field-tree__row--collapsible",
          activeId === item.id && "wsm-field-tree__row--active",
          item.errorCount > 0 && "wsm-field-tree__row--error",
          item.action && "wsm-field-tree__row--action",
        ].filter(Boolean).join(" ")}
        style={{
          "--field-tree-level": level,
          "--field-tree-indent": `${level * 28}px`,
        }}
      >
        {collapsible ? (
          <IconButton
            aria-label={`${expanded ? "Collapse" : "Expand"} ${item.label}`}
            aria-expanded={expanded}
            className="wsm-field-tree__toggle"
            icon={ChevronRight}
            onClick={() => onToggle(item.id)}
            size="compact"
            variant="ghost"
          />
        ) : null}
        <button
          aria-current={activeId === item.id ? "location" : undefined}
          className={["wsm-field-tree__target", item.hideIcon && "wsm-field-tree__target--no-icon"].filter(Boolean).join(" ")}
          onClick={() => onSelect(item)}
          type="button"
        >
          {!item.hideIcon && (
            <span className="wsm-field-tree__icon">{Icon ? <Icon aria-hidden="true" /> : <Circle aria-hidden="true" />}</span>
          )}
          <span className="wsm-field-tree__copy">
            <strong>{item.label}</strong>
            {item.description && <small>{item.description}</small>}
          </span>
          {item.errorCount > 0 ? (
            <span className="wsm-field-tree__state wsm-field-tree__state--error">
              <AlertCircle aria-hidden="true" />
              {item.errorCount}
            </span>
          ) : null}
        </button>
        {item.action && (
          <IconButton
            aria-label={item.action.label}
            className="wsm-field-tree__action"
            icon={item.action.icon}
            onClick={item.action.onClick}
            size="compact"
            variant="ghost"
          />
        )}
      </div>
      {hasChildren && expanded && (
        <ul>
          {children.map((child) => (
            <FieldTreeBranch
              activeId={activeId}
              collapsibleDepth={collapsibleDepth}
              item={child}
              key={child.id}
              level={level + 1}
              onSelect={onSelect}
              onToggle={onToggle}
              openIds={openIds}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

FieldTreeBranch.propTypes = {
  activeId: PropTypes.string,
  collapsibleDepth: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  openIds: PropTypes.instanceOf(Set).isRequired,
};

const FieldTree = ({ activeId, collapsibleDepth = -1, description, items, label = "Form fields", onSelect, title = "Form tree" }) => {
  const initiallyExpanded = useMemo(() => expandedIds(items), [items]);
  const [openIds, setOpenIds] = useState(() => new Set(initiallyExpanded));
  useEffect(() => {
    setOpenIds((current) => new Set([...current, ...initiallyExpanded]));
  }, [initiallyExpanded]);
  const errorCount = treeErrorCount(items);
  const toggle = (id) => setOpenIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return (
    <nav aria-label={label} className="wsm-field-tree">
      <div className="wsm-field-tree__heading">
        <span className="wsm-field-tree__heading-icon"><ListTree aria-hidden="true" /></span>
        <div className="wsm-field-tree__heading-copy">
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        {errorCount > 0 && <span className="wsm-field-tree__heading-errors"><AlertCircle aria-hidden="true" />{errorCount} errors</span>}
      </div>
      <ul className="wsm-field-tree__root">
        {items.map((item) => (
          <FieldTreeBranch
            activeId={activeId}
            collapsibleDepth={collapsibleDepth}
            item={item}
            key={item.id}
            level={0}
            onSelect={onSelect}
            onToggle={toggle}
            openIds={openIds}
          />
        ))}
      </ul>
    </nav>
  );
};

FieldTree.propTypes = {
  activeId: PropTypes.string,
  collapsibleDepth: PropTypes.number,
  description: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  label: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default FieldTree;
